import { ButtonType, ButtonWidget, InteractionState } from '@tableau/widgets';
import React from 'react';

import { Telemetry } from '~commonInfra/services/Telemetry';
import { LeftNavCollapseExpandClickEvent, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { selectLayoutType } from '~src/spacesPlatform/selectors/LayoutTypeSelector';

import { getAppStore } from '../../../bootstrap/redux/store/AppStore';
import { Messages } from '../../../codegen/Localize';
import { LeftNavVisibility } from './LeftNav';
import { NavStyles } from './NavigationStyles';

// When we have the mobile navigation dropdown, PanelState will include those states (open/close) as well
export type PanelState = LeftNavVisibility;

export const getCollapseLeftIcon = () => imgPath('spaces_toolbar_back_black.svg');
export const getCollapseLeftIconActive = () => imgPath('spaces_toolbar_back_black_active.svg');
export const getExpandRightIcon = () => imgPath('spaces_toolbar_forward.svg');
export const getExpandRightIconActive = () => imgPath('spaces_toolbar_forward_active.svg');

interface IconPaths {
  active: string;
  normal: string;
}

interface Icons {
  expanded: IconPaths;
  collapsed: IconPaths;
}

const getIcons: () => Icons = () => ({
  expanded: {
    active: getExpandRightIconActive(),
    normal: getExpandRightIcon()
  },
  collapsed: {
    active: getCollapseLeftIconActive(),
    normal: getCollapseLeftIcon()
  }
});

export interface NavPanelButtonProps {
  readonly panelState: PanelState;
  readonly visibleVerticalScroll: boolean;
  readonly panelOpenMessage: string;
  readonly toggleNavPanel: () => void;
}

/** The button to open/close/minimize the navigation panel, whether it's in a dropdown or in the sidebar/leftnav */
export class NavPanelButton extends React.Component<NavPanelButtonProps> {
  public render(): JSX.Element {
    return (
      <div className={NavStyles.LeftPanelButton}>
        <ButtonWidget
          buttonType={ButtonType.LowEmphasis}
          getContent={this.getButtonContent}
          title={this.isPanelOpen() ? Messages.leftNavToggleButtonCollapseTitle() : Messages.leftNavToggleButtonExpandTitle()}
          aria-label={this.isPanelOpen() ? Messages.leftNavToggleButtonCollapseAriaLabel() : Messages.leftNavToggleButtonExpandAriaLabel()}
          testId={this.isPanelOpen() ? 'close-left-nav' : 'open-left-nav'}
          handleClick={this.onClick}
          style={NavStyles.ButtonStyle}
        />
      </div>
    );
  }

  private onClick = () => {
    Telemetry.sendEvent<LeftNavCollapseExpandClickEvent>({
      type: TelemetryEvents.LeftNavCollapseExpandClick,
      action: this.isPanelOpen() ? 'collapse' : 'expand',
      layoutType: selectLayoutType(getAppStore().getState())
    });
    this.props.toggleNavPanel();
  };

  private getButtonContent = (interactiveState: InteractionState): JSX.Element => {
    const iconPaths = this.isPanelOpen() ? getIcons().collapsed : getIcons().expanded;
    return (
      <div className={NavStyles.getButtonContentClassNames(this.props.visibleVerticalScroll)}>
        <img
          src={interactiveState.hover || interactiveState.active ? iconPaths.active : iconPaths.normal}
          className={NavStyles.ButtonContentIcon}
          alt={this.isPanelOpen() ? Messages.leftNavToggleButtonCollapseTitle() : Messages.leftNavToggleButtonExpandTitle()}
        />
      </div>
    );
  };

  private isPanelOpen() {
    return this.props.panelState === 'expanded';
  }
}
